import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { useSelector } from 'react-redux';
import { SEND_PUSH_NOTIFICATION_TO_ALL, SEND_PUSH_NOTIFICATION_TO_MATES } from '../../../../graphql/mutations';
import { ALL_USERS, WORLD_CUP_MISSING_GUESS_REMINDERS } from '../../../../graphql/queries';
import Card from '../../../../components/Card/Card';
import Button from '../../../../components/Button/Button';
import Notification from '../../../../components/Notification/Notification';
import './PushBroadcastCard.css';

const CL_KEY = 'championsLeague';
const WC_KEY = 'worldCup2026';

const STEPS = [
  { id: 'audience', label: 'Hvem' },
  { id: 'message', label: 'Besked' },
  { id: 'destination', label: 'Destination' }
];

const AUDIENCE_PRESETS = [
  {
    id: 'all',
    label: 'Alle',
    desc: 'Alle der har notifikationer slået til i appen'
  },
  {
    id: 'cl',
    label: 'CL-spillere',
    desc: 'Kun dem der spiller Champions League'
  },
  {
    id: 'wc',
    label: 'VM-spillere',
    desc: 'Kun dem der spiller VM 2026'
  },
  {
    id: 'wc-missing',
    label: 'VM · mangler gæt',
    desc: 'Spillere uden gæt på kampe der starter inden 12 timer'
  },
  {
    id: 'custom',
    label: 'Vælg selv',
    desc: 'Vælg personer fra listen nedenfor'
  }
];

/** Klikbare stier som matcher `App.js` — grupperet for overskueligt link-valg. */
const PUSH_LINK_GROUPS = [
  {
    id: 'general',
    label: 'Generelt',
    items: [{ path: '/', label: 'Forside', desc: 'Appens forside' }]
  },
  {
    id: 'cl',
    label: 'Champions League',
    items: [{ path: '/champions-league', label: 'Champions League', desc: 'CL-spillet' }]
  },
  {
    id: 'wc',
    label: 'VM 2026',
    items: [
      { path: '/world-cup-2026', label: 'Oversigt', desc: 'VM 2026 forsiden' },
      { path: '/world-cup-2026/teams', label: 'Hold', desc: 'Grupper og hold' },
      { path: '/world-cup-2026/matches', label: 'Kampe', desc: 'Kommende og spillede kampe' },
      { path: '/world-cup-2026/my-guesses', label: 'Mine gæt', desc: 'Egne tip' },
      { path: '/world-cup-2026/leaderboard', label: 'Leaderboard', desc: 'Pointtavle' },
      { path: '/world-cup-2026/leagues', label: 'Grupper', desc: 'Private grupper' },
      { path: '/world-cup-2026/world-champion', label: 'Verdensmester', desc: 'Tip på vinder' }
    ]
  },
  {
    id: 'games',
    label: 'Andre spil',
    items: [
      { path: '/kampdag', label: 'Kampdag', desc: 'Kampdag-spillet' },
      { path: '/landskamp', label: 'Landskamp', desc: 'Landskamp-spillet' },
      { path: '/yatzy', label: 'Yatzy', desc: 'Yatzy-spillet' }
    ]
  },
  {
    id: 'account',
    label: 'Konto',
    items: [
      { path: '/profile', label: 'Profil', desc: 'Brugerens profil' },
      { path: '/admin', label: 'Admin', desc: 'Admin-panelet' }
    ]
  }
];

const PUSH_LINK_PRESETS = PUSH_LINK_GROUPS.flatMap((group) => group.items);

function getInitials(name, email) {
  const source = (name || email || '?').trim();
  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return source.slice(0, 2).toUpperCase();
}

function useNarrowViewport(maxWidth = 860) {
  const query = `(max-width: ${maxWidth}px)`;
  const [narrow, setNarrow] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia(query).matches : false
  );

  useEffect(() => {
    const mq = window.matchMedia(query);
    const onChange = (event) => setNarrow(event.matches);
    setNarrow(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [query]);

  return narrow;
}

function getSendResultNotification(sent, failed) {
  if (sent === 0) {
    if (failed > 0) {
      return {
        text: `Ingen modtog beskeden — ${failed} kunne ikke nås.`,
        type: 'error'
      };
    }
    return {
      text: 'Ingen modtog beskeden. Tjek at målgruppen har notifikationer slået til.',
      type: 'error'
    };
  }
  if (failed > 0) {
    return {
      text: `Beskeden er sendt til ${sent} person${sent === 1 ? '' : 'er'} (${failed} kunne ikke modtages).`,
      type: 'warning'
    };
  }
  return {
    text: `Beskeden er sendt til ${sent} person${sent === 1 ? '' : 'er'}.`,
    type: 'success'
  };
}

function fmtKickoffShort(iso) {
  return new Date(iso).toLocaleString('da-DK', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function buildMissingGuessMessage(reminder) {
  const matches = reminder?.matches || [];
  if (matches.length === 0) return '';
  if (matches.length === 1) {
    const match = matches[0];
    return `Husk at gætte på ${match.homeTeam} vs ${match.awayTeam} (${fmtKickoffShort(match.kickoffTime)})!`;
  }
  const firstKickoff = fmtKickoffShort(matches[0].kickoffTime);
  return `Du mangler gæt på ${matches.length} VM-kampe der starter inden for 12 timer (første kl. ${firstKickoff}). Gæt inden kickoff!`;
}

function getDestinationLabel(preset, group, trimmedUrl) {
  if (preset) {
    if (group?.id === 'wc') return `VM 2026 · ${preset.label}`;
    if (group?.id === 'cl') return 'Champions League';
    return preset.label;
  }
  if (trimmedUrl) return 'Eget link';
  return 'Ikke valgt';
}

export default function PushBroadcastCard() {
  const isNarrow = useNarrowViewport();
  const isAdmin = useSelector((state) => state.mate.currentUser?.role === 'admin');
  const [audienceMode, setAudienceMode] = useState('all');
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [userFilter, setUserFilter] = useState('');
  const [title, setTitle] = useState('Svirr');
  const [body, setBody] = useState('');
  const [url, setUrl] = useState('/champions-league');
  const [notification, setNotification] = useState({ open: false, message: '', type: 'info' });

  const showNotification = useCallback((message, type = 'info') => {
    setNotification({ open: true, message, type });
  }, []);

  const handleCloseNotification = useCallback(() => {
    setNotification((prev) => ({ ...prev, open: false }));
  }, []);

  const { data: usersData, loading: usersLoading } = useQuery(ALL_USERS, {
    skip: !isAdmin
  });

  const {
    data: missingGuessData,
    loading: missingGuessLoading,
    refetch: refetchMissingGuesses,
  } = useQuery(WORLD_CUP_MISSING_GUESS_REMINDERS, {
    skip: !isAdmin || audienceMode !== 'wc-missing',
    variables: { hours: 12 },
    fetchPolicy: 'cache-and-network',
  });

  const missingGuessReminder = missingGuessData?.worldCupMissingGuessReminders;
  const missingGuessMatches = useMemo(
    () => missingGuessReminder?.matches || [],
    [missingGuessReminder?.matches]
  );
  const missingGuessMates = useMemo(
    () => missingGuessReminder?.mates || [],
    [missingGuessReminder?.mates]
  );
  const missingGuessMatchById = useMemo(() => {
    const map = new Map();
    missingGuessMatches.forEach((match) => map.set(match.id, match));
    return map;
  }, [missingGuessMatches]);

  const mates = useMemo(() => usersData?.allUsers || [], [usersData?.allUsers]);

  const filteredMates = useMemo(() => {
    const q = userFilter.trim().toLowerCase();
    if (!q) return mates;
    return mates.filter(
      (m) =>
        (m.name || '').toLowerCase().includes(q) ||
        (m.email || '').toLowerCase().includes(q)
    );
  }, [mates, userFilter]);

  const scope = audienceMode === 'all' ? 'all' : 'selected';

  const toggleOne = useCallback((id) => {
    setAudienceMode((mode) => (mode === 'wc-missing' ? mode : 'custom'));
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const selectByPageAccess = useCallback((pageKey) => {
    const ids = mates.filter((m) => (m.pageAccess || []).includes(pageKey)).map((m) => m.id);
    setSelectedIds(new Set(ids));
  }, [mates]);

  const handleAudiencePreset = useCallback((presetId) => {
    setAudienceMode(presetId);
    if (presetId === 'all') {
      setSelectedIds(new Set());
      return;
    }
    if (presetId === 'cl') {
      selectByPageAccess(CL_KEY);
      return;
    }
    if (presetId === 'wc') {
      selectByPageAccess(WC_KEY);
      return;
    }
    if (presetId === 'wc-missing') {
      setUrl('/world-cup-2026/matches');
      setTitle('VM 2026');
      return;
    }
    if (presetId === 'custom') {
      setSelectedIds(new Set());
    }
  }, [selectByPageAccess]);

  useEffect(() => {
    if (audienceMode !== 'wc-missing') return;
    if (!missingGuessMates.length) {
      setSelectedIds(new Set());
      return;
    }
    setSelectedIds(new Set(missingGuessMates.map((mate) => mate.userId)));
  }, [audienceMode, missingGuessMates]);

  useEffect(() => {
    if (audienceMode !== 'wc-missing') return;
    const suggested = buildMissingGuessMessage(missingGuessReminder);
    if (!suggested) return;
    setBody((current) => (current.trim() ? current : suggested));
  }, [audienceMode, missingGuessReminder]);

  const selectAllVisible = useCallback(() => {
    setAudienceMode('custom');
    setSelectedIds((prev) => {
      const next = new Set(prev);
      filteredMates.forEach((m) => next.add(m.id));
      return next;
    });
  }, [filteredMates]);

  const clearSelection = useCallback(() => {
    setAudienceMode('custom');
    setSelectedIds(new Set());
  }, []);

  const [sendToAll, { loading: loadingAll }] = useMutation(SEND_PUSH_NOTIFICATION_TO_ALL, {
    onCompleted: (data) => {
      const { sent, failed } = data.sendPushNotificationToAll;
      const result = getSendResultNotification(sent, failed);
      showNotification(result.text, result.type);
    },
    onError: (err) => {
      showNotification(err.message || 'Noget gik galt — prøv igen.', 'error');
    }
  });

  const [sendToMates, { loading: loadingMates }] = useMutation(SEND_PUSH_NOTIFICATION_TO_MATES, {
    onCompleted: (data) => {
      const { sent, failed } = data.sendPushNotificationToMates;
      const result = getSendResultNotification(sent, failed);
      showNotification(result.text, result.type);
    },
    onError: (err) => {
      showNotification(err.message || 'Noget gik galt — prøv igen.', 'error');
    }
  });

  const loading = loadingAll || loadingMates;

  if (!isAdmin) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    handleCloseNotification();
    const vars = {
      title: title.trim() || 'Svirr',
      body: body.trim(),
      url: url.trim() || '/'
    };
    if (scope === 'all') {
      sendToAll({ variables: vars });
    } else {
      const mateIds = Array.from(selectedIds);
      if (mateIds.length === 0) {
        showNotification('Vælg mindst én person under «Hvem skal have beskeden?».', 'error');
        return;
      }
      sendToMates({ variables: { ...vars, mateIds } });
    }
  };

  const trimmedUrl = url.trim();
  const activePreset = PUSH_LINK_PRESETS.find((p) => p.path === trimmedUrl);
  const activeLinkGroup = PUSH_LINK_GROUPS.find((group) =>
    group.items.some((item) => item.path === trimmedUrl)
  );
  const destinationLabel = getDestinationLabel(activePreset, activeLinkGroup, trimmedUrl);

  const canSubmit = body.trim() && (scope === 'all' || selectedIds.size > 0);
  const previewTitle = title.trim() || 'Svirr';
  const previewBody = body.trim() || 'Skriv beskeden her…';

  const audienceSummary =
    audienceMode === 'all'
      ? 'Alle med notifikationer'
      : audienceMode === 'cl'
        ? `${selectedIds.size} CL-spiller${selectedIds.size === 1 ? '' : 'e'}`
        : audienceMode === 'wc'
          ? `${selectedIds.size} VM-spiller${selectedIds.size === 1 ? '' : 'e'}`
          : audienceMode === 'wc-missing'
            ? missingGuessLoading
              ? 'Henter manglende gæt…'
              : missingGuessMatches.length === 0
                ? 'Ingen kampe inden 12 timer'
                : selectedIds.size === 0
                  ? 'Alle har gættet'
                  : `${selectedIds.size} mangler gæt`
            : selectedIds.size === 0
              ? 'Ingen valgt endnu'
              : `${selectedIds.size} valgt person${selectedIds.size === 1 ? '' : 'er'}`;

  const submitHint = !body.trim()
    ? 'Skriv en besked først'
    : scope === 'selected' && selectedIds.size === 0
      ? 'Vælg hvem der skal have beskeden'
      : null;

  const completedSteps = [
    scope === 'all' || selectedIds.size > 0,
    Boolean(body.trim()),
    Boolean(trimmedUrl)
  ];

  return (
    <div className="push-broadcast-wrap">
      <Card className="push-broadcast-card" padding="large" hover={false}>
        <header className="push-broadcast-card__header">
          <div className="push-broadcast-card__header-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
              <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M13.73 21a2 2 0 0 1-3.46 0" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <div className="push-broadcast-card__header-text">
            <h2 className="push-broadcast-card__title">Send besked til telefonen</h2>
            <p className="push-broadcast-card__hint">
              Send en notifikation til Svirr-brugere. Følg de tre trin nedenfor — det tager under et minut.
            </p>
          </div>
        </header>

        <ol className="push-broadcast-card__steps" aria-label="Trin">
          {STEPS.map((step, index) => (
            <li
              key={step.id}
              className={`push-broadcast-card__step${completedSteps[index] ? ' push-broadcast-card__step--done' : ''}`}
            >
              <span className="push-broadcast-card__step-num" aria-hidden="true">
                {completedSteps[index] ? '✓' : index + 1}
              </span>
              <span className="push-broadcast-card__step-label">{step.label}</span>
            </li>
          ))}
        </ol>

        <form onSubmit={handleSubmit} className="push-broadcast-card__form">
          <div className="push-broadcast-card__layout">
            <section
              className="push-broadcast-card__panel push-broadcast-card__panel--audience"
              aria-labelledby="push-audience-heading"
            >
              <div className="push-broadcast-card__panel-head">
                <span className="push-broadcast-card__step-badge">1</span>
                <div>
                  <h3 id="push-audience-heading" className="push-broadcast-card__panel-title">
                    Hvem skal have beskeden?
                  </h3>
                  <p className="push-broadcast-card__panel-desc">
                    Kun brugere der har slået notifikationer til, modtager beskeden.
                  </p>
                </div>
              </div>

              <div className="push-broadcast-card__audience-presets" role="radiogroup" aria-label="Modtagere">
                {AUDIENCE_PRESETS.map((preset) => {
                  const active = audienceMode === preset.id;
                  return (
                    <button
                      key={preset.id}
                      type="button"
                      role="radio"
                      aria-checked={active}
                      className={`push-broadcast-card__audience-preset${active ? ' push-broadcast-card__audience-preset--active' : ''}`}
                      onClick={() => handleAudiencePreset(preset.id)}
                    >
                      <span className="push-broadcast-card__audience-preset-label">{preset.label}</span>
                      <span className="push-broadcast-card__audience-preset-desc">{preset.desc}</span>
                    </button>
                  );
                })}
              </div>

              {audienceMode === 'custom' && (
                <div className="push-broadcast-card__picker">
                  <p className="push-broadcast-card__picker-intro">Tryk på en person for at vælge eller fravælge.</p>
                  <div className="push-broadcast-card__toolbar">
                    <div className="push-broadcast-card__search">
                      <svg className="push-broadcast-card__search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                        <circle cx="11" cy="11" r="8" />
                        <path d="m21 21-4.35-4.35" strokeLinecap="round" />
                      </svg>
                      <input
                        type="search"
                        className="push-broadcast-card__search-input"
                        placeholder="Søg på navn eller e-mail…"
                        value={userFilter}
                        onChange={(e) => setUserFilter(e.target.value)}
                        aria-label="Søg brugere"
                      />
                    </div>
                    <span className="push-broadcast-card__count-badge">
                      {usersLoading ? 'Henter…' : `${selectedIds.size} valgt`}
                    </span>
                  </div>

                  <div className="push-broadcast-card__quick-actions" role="group" aria-label="Hurtigvalg">
                    <button type="button" className="push-broadcast-card__chip" onClick={selectAllVisible}>
                      Vælg alle i listen
                    </button>
                    <button type="button" className="push-broadcast-card__chip push-broadcast-card__chip--muted" onClick={clearSelection}>
                      Fravælg alle
                    </button>
                  </div>

                  <ul className="push-broadcast-card__user-list" role="list" aria-busy={usersLoading}>
                    {usersLoading && (
                      <li className="push-broadcast-card__empty">Henter brugere…</li>
                    )}
                    {!usersLoading && filteredMates.map((m) => {
                      const selected = selectedIds.has(m.id);
                      return (
                        <li
                          key={m.id}
                          className={`push-broadcast-card__user-row${selected ? ' push-broadcast-card__user-row--on' : ''}`}
                        >
                          <label className="push-broadcast-card__user-label">
                            <input
                              type="checkbox"
                              className="push-broadcast-card__checkbox"
                              checked={selected}
                              onChange={() => toggleOne(m.id)}
                            />
                            <span className="push-broadcast-card__avatar" aria-hidden="true">
                              {getInitials(m.name, m.email)}
                            </span>
                            <span className="push-broadcast-card__user-main">
                              <span className="push-broadcast-card__user-line">
                                <span className="push-broadcast-card__user-name">{m.name || 'Uden navn'}</span>
                                {(m.pageAccess || []).includes(CL_KEY) && (
                                  <span className="push-broadcast-card__badge">CL</span>
                                )}
                                {(m.pageAccess || []).includes(WC_KEY) && (
                                  <span className="push-broadcast-card__badge push-broadcast-card__badge--wc">VM</span>
                                )}
                              </span>
                              <span className="push-broadcast-card__user-email">{m.email}</span>
                            </span>
                            {selected && (
                              <span className="push-broadcast-card__check-mark" aria-hidden="true">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                  <polyline points="20 6 9 17 4 12" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                              </span>
                            )}
                          </label>
                        </li>
                      );
                    })}
                  </ul>
                  {!usersLoading && filteredMates.length === 0 && (
                    <p className="push-broadcast-card__empty">Ingen brugere matcher søgningen.</p>
                  )}
                </div>
              )}

              {audienceMode === 'all' && (
                <div className="push-broadcast-card__all-note">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden="true">
                    <circle cx="12" cy="12" r="10" />
                    <path d="M12 16v-4M12 8h.01" strokeLinecap="round" />
                  </svg>
                  <p>Alle med notifikationer får beskeden — uanset hvilke spil de deltager i.</p>
                </div>
              )}

              {(audienceMode === 'cl' || audienceMode === 'wc') && !usersLoading && (
                <div className="push-broadcast-card__all-note">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden="true">
                    <circle cx="12" cy="12" r="10" />
                    <path d="M12 16v-4M12 8h.01" strokeLinecap="round" />
                  </svg>
                  <p>
                    {selectedIds.size === 0
                      ? `Ingen ${audienceMode === 'cl' ? 'CL' : 'VM'}-spillere fundet. Prøv «Vælg selv» i stedet.`
                      : `${selectedIds.size} ${audienceMode === 'cl' ? 'CL' : 'VM'}-spiller${selectedIds.size === 1 ? '' : 'e'} er valgt.`}
                  </p>
                </div>
              )}

              {audienceMode === 'wc-missing' && (
                <div className="push-broadcast-card__missing-guesses">
                  <div className="push-broadcast-card__missing-guesses-head">
                    <p className="push-broadcast-card__missing-guesses-intro">
                      Kampe med kickoff inden for 12 timer, hvor spillere endnu kan gætte.
                    </p>
                    <button
                      type="button"
                      className="push-broadcast-card__chip"
                      onClick={() => refetchMissingGuesses()}
                      disabled={missingGuessLoading}
                    >
                      {missingGuessLoading ? 'Opdaterer…' : 'Opdatér liste'}
                    </button>
                  </div>

                  {missingGuessLoading && !missingGuessReminder ? (
                    <p className="push-broadcast-card__empty">Henter kampe og manglende gæt…</p>
                  ) : null}

                  {!missingGuessLoading && missingGuessMatches.length === 0 ? (
                    <div className="push-broadcast-card__all-note">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden="true">
                        <circle cx="12" cy="12" r="10" />
                        <path d="M12 16v-4M12 8h.01" strokeLinecap="round" />
                      </svg>
                      <p>Der er ingen kommende kampe inden for de næste 12 timer.</p>
                    </div>
                  ) : null}

                  {missingGuessMatches.length > 0 ? (
                    <>
                      <div className="push-broadcast-card__missing-matches">
                        <span className="push-broadcast-card__missing-matches-label">
                          {missingGuessMatches.length} kamp{missingGuessMatches.length === 1 ? '' : 'e'} inden 12 timer
                        </span>
                        <ul className="push-broadcast-card__missing-match-list" role="list">
                          {missingGuessMatches.map((match) => (
                            <li key={match.id} className="push-broadcast-card__missing-match-item">
                              <span className="push-broadcast-card__missing-match-teams">
                                {match.homeTeam} vs {match.awayTeam}
                              </span>
                              <span className="push-broadcast-card__missing-match-meta">
                                {fmtKickoffShort(match.kickoffTime)}
                                {match.stage ? ` · ${match.stage}` : ''}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      {missingGuessMates.length === 0 ? (
                        <div className="push-broadcast-card__all-note push-broadcast-card__all-note--success">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden="true">
                            <circle cx="12" cy="12" r="10" />
                            <path d="M9 12l2 2 4-4" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                          <p>Alle VM-spillere har gættet på de kommende kampe.</p>
                        </div>
                      ) : (
                        <div className="push-broadcast-card__picker push-broadcast-card__picker--missing">
                          <p className="push-broadcast-card__picker-intro">
                            {missingGuessMates.length} person{missingGuessMates.length === 1 ? '' : 'er'} mangler gæt — fravælg dem du ikke vil sende til.
                          </p>
                          <span className="push-broadcast-card__count-badge">
                            {selectedIds.size} valgt
                          </span>
                          <ul className="push-broadcast-card__user-list" role="list">
                            {missingGuessMates.map((mate) => {
                              const selected = selectedIds.has(mate.userId);
                              const missingLabels = mate.missingMatchIds
                                .map((matchId) => missingGuessMatchById.get(matchId))
                                .filter(Boolean)
                                .map((match) => `${match.homeTeam} vs ${match.awayTeam}`);
                              return (
                                <li
                                  key={mate.userId}
                                  className={`push-broadcast-card__user-row${selected ? ' push-broadcast-card__user-row--on' : ''}`}
                                >
                                  <label className="push-broadcast-card__user-label">
                                    <input
                                      type="checkbox"
                                      className="push-broadcast-card__checkbox"
                                      checked={selected}
                                      onChange={() => toggleOne(mate.userId)}
                                    />
                                    <span className="push-broadcast-card__avatar" aria-hidden="true">
                                      {getInitials(mate.userName, mate.email)}
                                    </span>
                                    <span className="push-broadcast-card__user-main">
                                      <span className="push-broadcast-card__user-line">
                                        <span className="push-broadcast-card__user-name">{mate.userName}</span>
                                        <span className="push-broadcast-card__badge push-broadcast-card__badge--warn">
                                          {mate.missingCount} mangler
                                        </span>
                                      </span>
                                      <span className="push-broadcast-card__user-email">{mate.email}</span>
                                      {missingLabels.length > 0 ? (
                                        <span className="push-broadcast-card__missing-match-names">
                                          {missingLabels.join(' · ')}
                                        </span>
                                      ) : null}
                                    </span>
                                  </label>
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      )}
                    </>
                  ) : null}
                </div>
              )}
            </section>

            <section
              className="push-broadcast-card__panel push-broadcast-card__panel--message"
              aria-labelledby="push-message-heading"
            >
              <div className="push-broadcast-card__panel-head">
                <span className="push-broadcast-card__step-badge">2</span>
                <div>
                  <h3 id="push-message-heading" className="push-broadcast-card__panel-title">
                    Hvad skal der stå?
                  </h3>
                  <p className="push-broadcast-card__panel-desc">
                    Skriv beskeden som folk ser på deres telefon.
                  </p>
                </div>
              </div>

              <div className="push-broadcast-card__preview" aria-label="Forhåndsvisning — sådan ser beskeden ud på telefonen">
                <p className="push-broadcast-card__preview-label">Forhåndsvisning på telefon</p>
                <div className="push-broadcast-card__preview-phone">
                  <div className="push-broadcast-card__preview-toast">
                    <div className="push-broadcast-card__preview-app">
                      <span className="push-broadcast-card__preview-icon">S</span>
                      <span className="push-broadcast-card__preview-app-name">Svirr</span>
                      <span className="push-broadcast-card__preview-time">nu</span>
                    </div>
                    <p className="push-broadcast-card__preview-title">{previewTitle}</p>
                    <p className={`push-broadcast-card__preview-body${!body.trim() ? ' push-broadcast-card__preview-body--placeholder' : ''}`}>
                      {previewBody}
                    </p>
                  </div>
                </div>
              </div>

              <div className="push-broadcast-card__fields">
                <div className="push-broadcast-card__field">
                  <label htmlFor="push-title">Titel</label>
                  <input
                    id="push-title"
                    className="push-broadcast-card__input"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="Svirr"
                  />
                  <p className="push-broadcast-card__field-hint">
                    Vises øverst i notifikationen — de fleste lader den stå som «Svirr».
                  </p>
                </div>
                <div className="push-broadcast-card__field">
                  <label htmlFor="push-body">
                    Besked
                    <span className="push-broadcast-card__field-meta">{body.length} tegn</span>
                  </label>
                  <textarea
                    id="push-body"
                    className="push-broadcast-card__textarea"
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    placeholder="Fx: Husk at gætte inden kampstart i aften!"
                    rows={4}
                    required
                  />
                </div>
              </div>
            </section>

            <section
              className="push-broadcast-card__panel push-broadcast-card__panel--destination"
              aria-labelledby="push-destination-heading"
            >
              <div className="push-broadcast-card__panel-head">
                <span className="push-broadcast-card__step-badge">3</span>
                <div>
                  <h3 id="push-destination-heading" className="push-broadcast-card__panel-title">
                    Hvor skal de lande?
                  </h3>
                  <p className="push-broadcast-card__panel-desc">
                    Når nogen trykker på beskeden, åbner den denne side i appen.
                  </p>
                </div>
              </div>

              <div
                className={`push-broadcast-card__destination-picked${
                  activePreset ? ' push-broadcast-card__destination-picked--selected' : ''
                }`}
                aria-live="polite"
              >
                <span className="push-broadcast-card__destination-picked-label">Valgt destination</span>
                <strong className="push-broadcast-card__destination-picked-value">{destinationLabel}</strong>
                {activePreset?.desc && (
                  <span className="push-broadcast-card__destination-picked-desc">{activePreset.desc}</span>
                )}
              </div>

              <div
                id="push-link-groups"
                className="push-broadcast-card__link-groups"
                role="group"
                aria-label="Vælg destination"
              >
                {PUSH_LINK_GROUPS.map((group) => {
                  const groupActive = group.items.some((item) => item.path === trimmedUrl);
                  const groupClassName = `push-broadcast-card__link-group${
                    groupActive ? ' push-broadcast-card__link-group--has-active' : ''
                  }`;

                  const groupTitle = (
                    <>
                      <span>{group.label}</span>
                      <span className="push-broadcast-card__link-group-count">
                        {group.items.length} {group.items.length === 1 ? 'side' : 'sider'}
                      </span>
                    </>
                  );

                  const groupItems = (
                    <div className="push-broadcast-card__link-group-items">
                      {group.items.map((item) => {
                        const active = trimmedUrl === item.path;
                        return (
                          <button
                            key={item.path}
                            type="button"
                            className={`push-broadcast-card__link-option${
                              active ? ' push-broadcast-card__link-option--active' : ''
                            }`}
                            onClick={() => setUrl(item.path)}
                            aria-pressed={active}
                          >
                            <span className="push-broadcast-card__link-option-label">{item.label}</span>
                            {item.desc && (
                              <span className="push-broadcast-card__link-option-desc">{item.desc}</span>
                            )}
                            {!isNarrow && (
                              <span className="push-broadcast-card__link-option-path">{item.path}</span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  );

                  if (isNarrow) {
                    return (
                      <details
                        key={group.id}
                        className={groupClassName}
                      >
                        <summary className="push-broadcast-card__link-group-title">{groupTitle}</summary>
                        {groupItems}
                      </details>
                    );
                  }

                  return (
                    <section key={group.id} className={groupClassName} aria-label={group.label}>
                      <h4 className="push-broadcast-card__link-group-title push-broadcast-card__link-group-title--static">
                        {groupTitle}
                      </h4>
                      {groupItems}
                    </section>
                  );
                })}
              </div>

              <details className="push-broadcast-card__advanced">
                <summary className="push-broadcast-card__advanced-summary">Avanceret: skriv egen sti</summary>
                <div className="push-broadcast-card__advanced-body">
                  <p className="push-broadcast-card__field-hint">
                    Kun nødvendigt hvis destinationen ikke findes i listen ovenfor.
                  </p>
                  <div className="push-broadcast-card__field">
                    <label htmlFor="push-url">Sti i appen</label>
                    <input
                      id="push-url"
                      className="push-broadcast-card__input push-broadcast-card__input--mono"
                      value={url}
                      onChange={(e) => setUrl(e.target.value)}
                      placeholder="/champions-league"
                      aria-describedby="push-link-groups"
                    />
                  </div>
                </div>
              </details>
            </section>
          </div>

          <footer className="push-broadcast-card__footer">
            <div className="push-broadcast-card__footer-recap">
              <span className="push-broadcast-card__footer-label">Opsummering</span>
              <ul className="push-broadcast-card__footer-list">
                <li>
                  <span>Til</span>
                  <strong>{audienceSummary}</strong>
                </li>
                <li>
                  <span>Åbner</span>
                  <strong>{destinationLabel}</strong>
                </li>
                <li>
                  <span>Besked</span>
                  <strong>{body.trim() ? `${body.trim().slice(0, 48)}${body.trim().length > 48 ? '…' : ''}` : 'Mangler'}</strong>
                </li>
              </ul>
              {submitHint && (
                <p className="push-broadcast-card__footer-hint" role="status">{submitHint}</p>
              )}
            </div>
            <Button
              type="submit"
              title={loading ? 'Sender…' : 'Send besked'}
              disabled={loading || !canSubmit}
              loading={loading}
            />
          </footer>
        </form>

      </Card>

      <Notification
        open={notification.open}
        onClose={handleCloseNotification}
        message={notification.message}
        type={notification.type}
      />
    </div>
  );
}
